// src/components/blog/ArticleCard.tsx
//
// Card used on the blog index and related-articles rail. The card is
// fully clickable via a Next.js <Link> wrapping the whole surface.

import Link from 'next/link'
import { Clock, User } from 'lucide-react'
import type { BlogPostMeta } from '@/lib/content/blog-types'
import { formatBlogDate } from '@/lib/content/blog-types'

const CATEGORY_COLORS: Record<string, string> = {
  Math: 'bg-brand/15 text-brand-ink border-brand/30',
  'Reading & Writing': 'bg-brand/15 text-brand-ink border-brand/30',
  Strategy: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
  News: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  Product: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30',
}

export interface ArticleCardProps {
  post: BlogPostMeta
  featured?: boolean
}

export default function ArticleCard({ post, featured = false }: ArticleCardProps) {
  const categoryClass =
    CATEGORY_COLORS[post.category] ?? 'bg-muted text-muted-foreground border-border'

  return (
    <Link
      href={`/blog/${post.slug}`}
      className={
        'group flex flex-col overflow-hidden rounded-2xl border border-border bg-card/40 transition hover:border-brand/40 hover:bg-card/60 ' +
        (featured ? 'md:col-span-2 md:flex-row' : '')
      }
    >
      <div
        className={
          'relative overflow-hidden bg-muted ' +
          (featured ? 'h-48 w-full md:h-auto md:w-1/2' : 'h-44 w-full')
        }
      >
        {/* Using <img> so we avoid Next/Image config for external Unsplash URLs */}
        {}
        <img
          src={post.coverImage}
          alt=""
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div
        className={'flex flex-1 flex-col gap-3 p-5 ' + (featured ? 'md:justify-center md:p-8' : '')}
      >
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-medium ${categoryClass}`}
          >
            {post.category}
          </span>
          {post.series && post.seriesPart && (
            <span className="inline-flex items-center rounded-full border border-brand/30 bg-brand/10 px-2.5 py-0.5 font-medium text-brand-ink">
              {post.series} · Part {post.seriesPart}
            </span>
          )}
          <span className="text-muted-foreground">{formatBlogDate(post.date)}</span>
        </div>
        <h3
          className={
            'font-semibold leading-tight text-foreground group-hover:text-brand-ink ' +
            (featured ? 'text-2xl md:text-3xl' : 'text-lg')
          }
        >
          {post.title}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-3">{post.excerpt}</p>
        <div className="mt-auto flex items-center gap-4 pt-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <User className="h-3.5 w-3.5" aria-hidden />
            {post.author}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" aria-hidden />
            {post.readingMinutes} min read
          </span>
        </div>
      </div>
    </Link>
  )
}
